import React from "react";
import { Box, Typography } from "@mui/material";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import GlassPanel from "./ui/GlassPanel";
import MagneticButton from "./ui/MagneticButton";
import TiltCard from "./ui/TiltCard";

export default function MarketingStrategy() {
  const navigate = useNavigate();

  const handleLearnMore = () => {
    navigate("/marketing-details");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: { xs: "40px 20px", md: "80px 40px" },
        backgroundColor: "#F8F8FB",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: "10%",
          right: "-8%",
          width: 460,
          height: 460,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,95,109,0.1) 0%, rgba(255,95,109,0) 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Main Heading */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6 }}
        style={{ position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            display: "inline-block",
            px: 2,
            py: 0.5,
            mb: 2,
            borderRadius: 999,
            background: "rgba(253,129,18,0.1)",
            border: "1px solid rgba(253,129,18,0.25)",
          }}
        >
          <Typography sx={{ color: "#C85E00", fontWeight: 700, fontSize: "0.8rem", letterSpacing: 0.5 }}>
            WHAT WE DO
          </Typography>
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: "700",
            fontFamily: "Poppins, sans-serif",
            mb: 6,
            color: "#14141F",
          }}
        >
          Comprehensive Marketing <Box component="span" sx={{ color: "#FD8112" }}>Solutions</Box>
        </Typography>
      </motion.div>

      {/* First Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1100px",
          mb: 8,
          gap: { xs: 5, md: 4 },
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left Text Section */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          style={{ flex: 1 }}
        >
          <Box sx={{ textAlign: { xs: "center", md: "left" }, pr: { md: "30px" } }}>
            <Typography variant="h5" sx={{ fontWeight: "600", fontFamily: "Poppins, sans-serif", mb: 2, color: "#14141F" }}>
              Design Your Ideal Marketing Strategy
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 3, fontFamily: "Nunito, serif" }}>
              We help you track every campaign, uncover new engagement opportunities, and choose the right marketing tools.
              Our comprehensive approach ensures your marketing strategy aligns perfectly with your business goals.
            </Typography>
            <MagneticButton
              variant="contained"
              onClick={handleLearnMore}
              sx={{
                background: "linear-gradient(135deg, #FD8112, #FF5F6D)",
                color: "white",
                px: 4,
                py: 1.5,
                fontFamily: "Nunito, serif",
              }}
            >
              Learn More
            </MagneticButton>
          </Box>
        </motion.div>

        {/* Right Animation Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{ flex: 1, display: "flex", justifyContent: "center" }}
        >
          <Box sx={{ width: "100%", maxWidth: 500 }}>
            <TiltCard glareColor="#FD8112" maxTilt={7}>
              <GlassPanel
                sx={{
                  p: 2,
                  boxShadow: "0 30px 60px rgba(20,20,40,0.12)",
                  border: "1px solid rgba(255,255,255,0.8)",
                }}
              >
                <DotLottieReact
                  src="https://lottie.host/93325f68-c1d9-4088-8cfb-015cb4b6f03b/iAxFdL23rS.lottie"
                  loop
                  autoplay
                  style={{ width: "100%", height: "360px" }}
                />
              </GlassPanel>
            </TiltCard>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}
